/**
 * useCanvasSandbox — manages AppSandbox lifecycle for the Page Editor canvas.
 *
 * Creates one AppSandbox per app editing session. Mounts/unmounts on
 * canvasRef availability. Destroys on component unmount or appId change.
 *
 * Design: .planning/phases/151-sandbox-architecture-design/APPSANDBOX-UNIFIED-API.md
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createAppSandbox, type AppSandbox, type ThemeTokens } from '@cogo/react-renderer-v2'
import { getIsolatedAppStyle } from '@cogo/react-renderer'
import type { RefObject } from 'react'

interface UseCanvasSandboxOptions {
  appId: string | null | undefined
  tokens: ThemeTokens | null | undefined
  colorScheme: 'light' | 'dark'
  hostRef: RefObject<HTMLDivElement | null>
}

interface UseCanvasSandboxResult {
  sandbox: AppSandbox | null
  ready: boolean
}

export function useCanvasSandbox({
  appId,
  tokens,
  colorScheme,
  hostRef,
}: UseCanvasSandboxOptions): UseCanvasSandboxResult {
  const sandboxRef = useRef<AppSandbox | null>(null)
  const [sandbox, setSandbox] = useState<AppSandbox | null>(null)
  const [ready, setReady] = useState(false)
  // sandboxKey increments when sandbox is recreated, triggering the layout effect
  const [sandboxKey, setSandboxKey] = useState(0)

  // Create / destroy sandbox when appId changes
  useEffect(() => {
    if (!appId) {
      sandboxRef.current?.destroy()
      sandboxRef.current = null
      setSandbox(null)
      setReady(false)
      return
    }

    // Destroy previous sandbox if appId changed
    if (sandboxRef.current) {
      sandboxRef.current.unmount()
      sandboxRef.current.destroy()
      sandboxRef.current = null
    }

    const designTokens = getIsolatedAppStyle(tokens ?? undefined, colorScheme) as Record<string, string>
    sandboxRef.current = createAppSandbox({
      appId,
      designTokens,
      onNavigationAttempt: (url, type) => {
        console.warn(`[AppSandbox] app "${appId}" tried to navigate (${type}): ${url}`)
      },
    })
    setSandbox(sandboxRef.current)

    // Increment key so useLayoutEffect re-runs and calls mount()
    setSandboxKey((k) => k + 1)

    return () => {
      sandboxRef.current?.unmount()
      sandboxRef.current?.destroy()
      sandboxRef.current = null
      setSandbox(null)
      setReady(false)
    }
    // Only recreate sandbox when appId changes, not on token changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId])

  // Mount sandbox to the generated-app host after layout (so hostRef.current is populated).
  // sandboxKey ensures this re-runs after sandbox is recreated.
  useLayoutEffect(() => {
    const sandbox = sandboxRef.current
    const el = hostRef.current
    if (!sandbox || !el) return

    sandbox.mount(el)
    setReady(true)

    return () => {
      // unmount on cleanup (tab switch triggers this via React key-based remount)
      setReady(false)
      sandbox.unmount()
    }
  // Re-run when sandbox is recreated (sandboxKey changes) — hostRef is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxKey])

  // The Page canvas changes its outer layout when Studio moves between the
  // framed preview and the editor surface. React can replace the host DOM node
  // without remounting this hook or changing appId, so the sandboxKey effect
  // above does not run again. Repair that host boundary after every commit;
  // AppSandbox/DomSandbox mount is idempotent and preserves the exact portal
  // roots while re-parenting them into the current host.
  useLayoutEffect(() => {
    const currentSandbox = sandboxRef.current
    const currentHost = hostRef.current
    if (!currentSandbox || !currentHost || currentSandbox.state === 'destroyed') return

    const { renderRoot, portalContainer } = currentSandbox.modules.dom
    if (
      currentSandbox.state !== 'mounted' ||
      renderRoot?.parentElement !== currentHost ||
      portalContainer?.parentElement !== currentHost
    ) {
      currentSandbox.mount(currentHost)
    }
  })

  // Hot-swap design tokens when theme changes — does NOT recreate sandbox
  useEffect(() => {
    const sandbox = sandboxRef.current
    if (!sandbox || sandbox.state === 'created' || sandbox.state === 'destroyed') return
    const newTokens = getIsolatedAppStyle(tokens ?? undefined, colorScheme) as Record<string, string>
    sandbox.updateTokens(newTokens)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, colorScheme])

  return { sandbox, ready }
}
